/**
 * Iter 106b — Rescrew rate-row breakdown editor.
 *
 * Visual format intentionally mirrors the Stage-2 paint per-coat
 * breakdown modal (see `RestorationConcreteRepairs.Stage2PaintSection`)
 * so operators get a consistent edit experience across the builder.
 *
 * Layout:
 *   • Full-page overlay with emerald/teal gradient header, "Edit
 *     rescrew detail" eyebrow + Calculator-icon title.
 *   • Single-table body: one row per rescrew rate with editable
 *     inline cells (Work Type · Screw Size · Qty/m² · Tekscrew $ ·
 *     Materials $/m² · Labour $/m² · Contractor $/m² · Rate $/m²
 *     highlighted on the right).
 *   • +/− toggle expands a supply panel below the row with the
 *     tekscrew description, product code, supplier, hours/m², and
 *     live-computed Throughput / Implied $/hr cross-checks.
 *   • Save target radio in the body (Override this appraisal /
 *     Apply to master rate) — defaults to override.
 *   • Footer cancel + save (button colour switches based on target).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Calculator, X, Save, Loader2, AlertCircle } from 'lucide-react';

export default function RescrewDetailModal({ api, open, onOpenChange, rate, override, onSave }) {
  const [target, setTarget] = useState('override');
  const [draft, setDraft] = useState({});
  const [supplyOpen, setSupplyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Seed editable draft from override merged onto master values — the
  // operator sees the *effective* numbers, easier to reason about than
  // a side-by-side "master vs override" view.
  useEffect(() => {
    if (!open || !rate) return;
    setDraft({
      work_type:           override?.work_type        ?? rate.work_type        ?? '',
      screw_size:          override?.screw_size       ?? rate.screw_size       ?? '',
      materials_desc:      override?.materials_desc   ?? rate.materials_desc   ?? '',
      supplier_code:       override?.supplier_code    ?? rate.supplier_code    ?? '',
      supplier:            override?.supplier         ?? rate.supplier         ?? '',
      buy_cost:            override?.buy_cost         ?? rate.buy_per_lm       ?? 0,
      qty_per_m2:          override?.qty_per_m2       ?? rate.qty_per_m2       ?? 7,
      materials_rate:      override?.materials_rate   ?? rate.material_per_lm  ?? 0,
      labour_hours_per_m2: override?.labour_hours_per_m2 ?? rate.labour_hours_per_m2 ?? 0,
      labour_rate:         override?.labour_rate      ?? rate.labour_per_lm    ?? 0,
      contractor_rate:     override?.contractor_rate  ?? rate.contractor_per_lm?? 0,
    });
    setErr(null);
    setTarget('override');
    // Open supply panel automatically if there's supplier / code / desc
    // / hours data to show — otherwise leave collapsed so the row stays
    // compact like the paint modal default.
    const hasSupply = (override?.materials_desc || rate.materials_desc) ||
                      (override?.supplier_code  || rate.supplier_code) ||
                      (override?.labour_hours_per_m2 || rate.labour_hours_per_m2);
    setSupplyOpen(!!hasSupply);
  }, [open, rate, override]);

  // Derived sales rate per m² (live preview of what the operator's edit
  // will charge — same formula the calculator uses).
  const chargePerM2 = useMemo(() => {
    return Math.round(((Number(draft.materials_rate) || 0) + (Number(draft.labour_rate) || 0)) * 100) / 100;
  }, [draft.materials_rate, draft.labour_rate]);
  // Tekscrew cost per m² = qty × buy_each.
  const tekScrewCostPerM2 = useMemo(() => {
    return Math.round((Number(draft.qty_per_m2) || 0) * (Number(draft.buy_cost) || 0) * 100) / 100;
  }, [draft.qty_per_m2, draft.buy_cost]);
  const lossWarning = tekScrewCostPerM2 > 0 && Number(draft.materials_rate) > 0 && tekScrewCostPerM2 > Number(draft.materials_rate);
  // Implied $/hr cross-checks (same shape as the paint modal). Operator
  // records `labour_hours_per_m2`; we expose: hours × labour_rate
  // (labour cost per m²) and contractor_per_m² / hours_per_m² (implied
  // contractor $/hr). Helps sanity-check the rate is in line with
  // typical hourly labour bands.
  const hoursPerM2 = Number(draft.labour_hours_per_m2) || 0;
  const labourPerHr     = hoursPerM2 > 0 ? (Number(draft.labour_rate) || 0) / hoursPerM2 : 0;
  const contractorPerHr = hoursPerM2 > 0 ? (Number(draft.contractor_rate) || 0) / hoursPerM2 : 0;

  const patchField = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const handleSave = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (target === 'master') {
        const body = { ...draft };
        if (body.qty_per_m2 != null) { body.ratio = body.qty_per_m2; delete body.qty_per_m2; }
        await api.patch(`/restoration/iron-repairs/rate-detail/${rate.id}`, body);
        await onSave({ override: {}, masterUpdated: true });
      } else {
        await onSave({ override: { ...draft }, masterUpdated: false });
      }
      onOpenChange(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open || !rate) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={() => onOpenChange(false)}
         data-testid="rescrew-detail-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl my-8" onClick={(e) => e.stopPropagation()}>
        {/* Header — same gradient + eyebrow as the paint breakdown editor */}
        <div className="px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Edit rescrew detail</div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mt-0.5">
              <Calculator size={18} className="text-emerald-700" /> {rate.name}
            </h3>
            <div className="text-[11px] text-slate-500 mt-0.5">Iron Restoration · Rescrew rate row</div>
            <div className="text-[11px] text-slate-600 mt-1">
              Sales rate per m² = <strong className="text-emerald-800">Material + Labour</strong>.
              Tekscrew $/each is the wholesale supplier cost (auto-feeds the per-m² material cost via Qty/m²).
              Contractor is sub-contracted labour cost. Both internal — they don't change the quote.
            </div>
          </div>
          <button type="button" onClick={() => onOpenChange(false)} className="text-slate-400 hover:text-slate-700" data-testid="rescrew-detail-close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {/* Save target — radio pair matching the layout of in-panel
              toggles elsewhere in the builder. */}
          <div className="bg-slate-50 border border-slate-200 rounded p-2 mb-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Save target</div>
            <label className="flex items-start gap-2 text-xs cursor-pointer py-1">
              <input type="radio" name="rescrew-detail-target" value="override" checked={target === 'override'}
                     onChange={() => setTarget('override')} data-testid="rescrew-detail-target-override"
                     className="mt-0.5 text-[#D35400]" />
              <div>
                <div className="font-semibold text-slate-800">Override this appraisal only</div>
                <div className="text-slate-500">Values land on the current quote — master rate row untouched. Other appraisals are unaffected.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 text-xs cursor-pointer py-1">
              <input type="radio" name="rescrew-detail-target" value="master" checked={target === 'master'}
                     onChange={() => setTarget('master')} data-testid="rescrew-detail-target-master"
                     className="mt-0.5 text-emerald-700" />
              <div>
                <div className="font-semibold text-slate-800">Apply to master rate</div>
                <div className="text-slate-500">Update the catalogue row — every appraisal that references this rate will pick up the change on next reload.</div>
              </div>
            </label>
          </div>

          {/* Editable breakdown table — single-row mirror of the paint
              modal's per-coat row layout. */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="rescrew-detail-table">
              <thead className="text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 pl-2 w-6"></th>
                  <th className="text-left py-2 pr-2 w-28">Work Type</th>
                  <th className="text-left py-2 pr-2 w-24">Screw Size</th>
                  <th className="text-right py-2 px-1.5 w-20" title="Screws per m² (operator default 7)">Qty/m²</th>
                  <th className="text-right py-2 px-1.5 w-24" title="Wholesale supplier cost per each">Tekscrew $</th>
                  <th className="text-right py-2 px-1.5 w-24" title="Sales rate billed for materials">Material $/m²</th>
                  <th className="text-right py-2 px-1.5 w-24" title="Sales rate billed for labour">Labour $/m²</th>
                  <th className="text-right py-2 px-1.5 w-24" title="Sub-contracted labour cost">Contractor $/m²</th>
                  <th className="text-right py-2 pl-2 w-28 bg-emerald-50">Rate (sales)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100" data-testid="rescrew-detail-row">
                  <td className="py-1.5 pl-2">
                    <button
                      type="button"
                      data-testid="rescrew-detail-supply-toggle"
                      onClick={() => setSupplyOpen(o => !o)}
                      title={supplyOpen ? 'Hide supply inputs' : 'Show supply inputs (tekscrew descr / code / hours)'}
                      className={`w-5 h-5 inline-flex items-center justify-center rounded text-xs font-bold transition-all ${
                        (draft.materials_desc || draft.supplier_code || draft.labour_hours_per_m2)
                          ? 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                      }`}
                    >{supplyOpen ? '−' : '+'}</button>
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      type="text"
                      value={draft.work_type || ''}
                      data-testid="rescrew-detail-work_type"
                      onChange={(e) => patchField('work_type', e.target.value)}
                      className="w-full px-1.5 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400 font-semibold text-slate-800"
                      placeholder="Rescrew"
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      type="text"
                      value={draft.screw_size || ''}
                      data-testid="rescrew-detail-screw_size"
                      onChange={(e) => patchField('screw_size', e.target.value)}
                      className="w-full px-1.5 py-1 text-xs border border-slate-300 rounded text-center font-mono focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                      placeholder="12*50"
                    />
                  </td>
                  <td className="py-1.5 px-1.5">
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={draft.qty_per_m2 ?? 0}
                      data-testid="rescrew-detail-qty_per_m2"
                      onChange={(e) => patchField('qty_per_m2', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                      className="w-full px-1.5 py-1 text-xs border border-slate-300 rounded text-right font-mono-num focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                    />
                  </td>
                  <td className="py-1.5 px-1.5">
                    <div className="relative">
                      <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={draft.buy_cost ?? 0}
                        data-testid="rescrew-detail-buy_cost"
                        onChange={(e) => patchField('buy_cost', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        className="w-full pl-4 pr-1.5 py-1 text-xs border border-slate-300 rounded text-right font-mono-num focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                      />
                    </div>
                  </td>
                  {['materials_rate', 'labour_rate', 'contractor_rate'].map((field) => (
                    <td key={field} className="py-1.5 px-1.5">
                      <div className="relative">
                        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={draft[field] ?? 0}
                          data-testid={`rescrew-detail-${field}`}
                          onChange={(e) => patchField(field, e.target.value === '' ? 0 : parseFloat(e.target.value))}
                          className="w-full pl-4 pr-1.5 py-1 text-xs border border-slate-300 rounded text-right font-mono-num focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
                        />
                      </div>
                    </td>
                  ))}
                  <td className="py-1.5 pl-2 text-right font-mono-num font-bold text-emerald-800 bg-emerald-50" data-testid="rescrew-detail-rate">
                    ${chargePerM2.toFixed(2)}
                  </td>
                </tr>

                {/* Supply panel — opens via +/− toggle, same look as
                    the paint modal's per-coat supply row. */}
                {supplyOpen && (
                  <tr className="border-b border-slate-100 bg-teal-50/40" data-testid="rescrew-detail-supply-row">
                    <td></td>
                    <td colSpan={8} className="py-2 pr-2 pl-2">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-teal-700 mb-1.5">
                        Tekscrew supply &amp; labour timing
                      </div>
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <label className="col-span-5 flex flex-col gap-0.5">
                          <span className="text-[10px] text-slate-500 font-semibold">Tekscrew descr.</span>
                          <input
                            type="text"
                            value={draft.materials_desc || ''}
                            data-testid="rescrew-detail-materials_desc"
                            onChange={(e) => patchField('materials_desc', e.target.value)}
                            className="px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-400"
                            placeholder="e.g. 12x50 Type17 Timberteks (TT12x50NC4)"
                          />
                        </label>
                        <label className="col-span-3 flex flex-col gap-0.5">
                          <span className="text-[10px] text-slate-500 font-semibold">Product code</span>
                          <input
                            type="text"
                            value={draft.supplier_code || ''}
                            data-testid="rescrew-detail-supplier_code"
                            onChange={(e) => patchField('supplier_code', e.target.value)}
                            className="px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-400 font-mono"
                            placeholder="TG-1250"
                          />
                        </label>
                        <label className="col-span-4 flex flex-col gap-0.5">
                          <span className="text-[10px] text-slate-500 font-semibold">Supplier</span>
                          <input
                            type="text"
                            value={draft.supplier || ''}
                            data-testid="rescrew-detail-supplier"
                            onChange={(e) => patchField('supplier', e.target.value)}
                            className="px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-400"
                            placeholder="Allfast"
                          />
                        </label>
                        <label className="col-span-3 flex flex-col gap-0.5">
                          <span className="text-[10px] text-slate-500 font-semibold">Hours per m²</span>
                          <div className="relative">
                            <input
                              type="number" step="0.01" min="0"
                              value={draft.labour_hours_per_m2 ?? 0}
                              data-testid="rescrew-detail-labour_hours_per_m2"
                              onChange={(e) => patchField('labour_hours_per_m2', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                              className="w-full pl-1.5 pr-9 py-1 text-xs border border-slate-300 rounded text-right font-mono-num focus:outline-none focus:ring-1 focus:ring-teal-400"
                            />
                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">hrs</span>
                          </div>
                        </label>
                        <div className="col-span-3 text-center bg-teal-100/70 rounded px-2 py-1">
                          <div className="text-[10px] text-slate-600 font-semibold">Tekscrew cost</div>
                          <div className="text-sm font-bold text-teal-900 font-mono-num" data-testid="rescrew-detail-tekscrew-cost">${tekScrewCostPerM2.toFixed(2)}/m²</div>
                          <div className="text-[9px] text-slate-500">{(Number(draft.qty_per_m2) || 0).toFixed(0)} × ${(Number(draft.buy_cost) || 0).toFixed(2)} ea</div>
                        </div>
                        <div className="col-span-3 text-center bg-teal-100/70 rounded px-2 py-1">
                          <div className="text-[10px] text-slate-600 font-semibold">Labour $/hr</div>
                          <div className="text-sm font-bold text-teal-900 font-mono-num" data-testid="rescrew-detail-labour-per-hr">{labourPerHr > 0 ? `$${labourPerHr.toFixed(2)}/hr` : '—'}</div>
                          <div className="text-[9px] text-slate-500">${(Number(draft.labour_rate) || 0).toFixed(2)}/m² ÷ {hoursPerM2 > 0 ? hoursPerM2.toFixed(2) : '—'} hr</div>
                        </div>
                        <div className="col-span-3 text-center bg-teal-100/70 rounded px-2 py-1">
                          <div className="text-[10px] text-slate-600 font-semibold">Contractor $/hr</div>
                          <div className="text-sm font-bold text-teal-900 font-mono-num" data-testid="rescrew-detail-contractor-per-hr">{contractorPerHr > 0 ? `$${contractorPerHr.toFixed(2)}/hr` : '—'}</div>
                          <div className="text-[9px] text-slate-500">${(Number(draft.contractor_rate) || 0).toFixed(2)}/m² ÷ {hoursPerM2 > 0 ? hoursPerM2.toFixed(2) : '—'} hr</div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 font-bold bg-slate-50">
                  <td colSpan={4} className="py-2 pl-2 text-right text-slate-700">Per-m² totals</td>
                  <td className="text-right py-2 px-1.5 font-mono-num text-slate-700">${tekScrewCostPerM2.toFixed(2)}</td>
                  <td className="text-right py-2 px-1.5 font-mono-num text-slate-700">${(Number(draft.materials_rate) || 0).toFixed(2)}</td>
                  <td className="text-right py-2 px-1.5 font-mono-num text-slate-700">${(Number(draft.labour_rate) || 0).toFixed(2)}</td>
                  <td className="text-right py-2 px-1.5 font-mono-num text-slate-700">${(Number(draft.contractor_rate) || 0).toFixed(2)}</td>
                  <td className="text-right py-2 pl-2 font-mono-num text-emerald-900 bg-emerald-100" data-testid="rescrew-detail-total-rate">${chargePerM2.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {lossWarning && (
            <div data-testid="rescrew-detail-loss-warning" className="mt-3 flex items-center gap-1.5 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
              <AlertCircle size={13} /> Tekscrew cost (${tekScrewCostPerM2.toFixed(2)}/m²) exceeds the materials rate (${(Number(draft.materials_rate) || 0).toFixed(2)}/m²) — you'll be selling at a loss on the screw alone.
            </div>
          )}
          {err && (
            <div data-testid="rescrew-detail-error" className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-1.5">
              <AlertCircle size={12} /> {err}
            </div>
          )}

          <div className="mt-4 text-[11px] text-slate-500 italic">
            {target === 'master'
              ? <>Note: this updates the canonical rate row used by <strong>every</strong> appraisal that references it. Cascades immediately.</>
              : <>Note: changes stay on this appraisal only — the master rate row remains untouched.</>}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button type="button" onClick={() => onOpenChange(false)} disabled={busy}
                  data-testid="rescrew-detail-cancel"
                  className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 rounded">Cancel</button>
          <button type="button" onClick={handleSave} disabled={busy}
                  data-testid="rescrew-detail-save"
                  className={`px-4 py-1.5 text-xs font-bold text-white rounded disabled:opacity-50 flex items-center gap-1.5 shadow-sm ${
                    target === 'master' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#D35400] hover:bg-[#A04000]'
                  }`}>
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            {busy ? 'Saving…' : (target === 'master' ? 'Apply to master rate' : 'Save override')}
          </button>
        </div>
      </div>
    </div>
  );
}
